import { computed, ref, watch } from 'vue'

type ErrorRecord = Record<string, string>

export function useServerValidation() {
  const errors = ref<ErrorRecord>({})

  function setFromResponse(data: Record<string, unknown>): boolean {
    const messages = data?.['message']
    if (!Array.isArray(messages)) return false

    const result: ErrorRecord = {}
    for (const msg of messages) {
      if (typeof msg !== 'string') continue
      // Split the message into key and value at the first space
      const index = msg.indexOf(' ')
      if (index === -1) continue
      result[msg.slice(0, index)] ??= msg.slice(index + 1)
    }

    errors.value = result
    return Object.keys(result).length > 0
  }

  function clear(key?: string) {
    if (!key) {
      errors.value = {}
    } else if (key in errors.value) {
      const next = { ...errors.value }
      delete next[key]
      errors.value = next
    }
  }

  function scope(prefix: string, format: (raw: ErrorRecord) => ErrorRecord = raw => raw) {
    const dotted = `${prefix}.`

    const scopedErrors = computed(() =>
      format(
        Object.fromEntries(
          Object.entries(errors.value)
            .filter(([key]) => key.startsWith(dotted))
            .map(([key, message]) => [key.slice(dotted.length), message])
        )
      )
    )

    function scopedClear(key?: string) {
      if (key) return clear(dotted + key)
      for (const key of Object.keys(errors.value)) {
        if (key.startsWith(dotted)) clear(key)
      }
    }

    function clearOnChange(source: () => Record<string, unknown> | null | undefined) {
      watch(
        () => ({ ...source() }),
        (next, prev) => {
          for (const key of new Set([...Object.keys(next), ...Object.keys(prev)])) {
            if (next[key] !== prev[key]) scopedClear(key)
          }
        }
      )
    }

    return { errors: scopedErrors, clear: scopedClear, clearOnChange }
  }

  return { errors, setFromResponse, clear, scope }
}
